import {
  Box,
  DialogTitle,
  Divider,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../../app/hooks";
import InfoTooltip from "../../../../../common/components/functional/InfoTooltip/InfoTooltip";
import { selectAssignments } from "../../../../talentProfile/Employment/AssignmentAdditionalInfo/AssignmentsSlice";
import {
  EditEntry,
  Entry,
  Leave,
  LeaveEntity,
  LeaveEntityDateRange,
} from "../../../entriesAPI";
import { filterAbsenceTypes } from "../../Shared/functions";
import { AbsenceListEntry } from "../AbsenceHoursList/AbsenceHoursList";
import ToGeneralLeave from "../ConvertToAbsence/ToGeneralLeave/ToGeneralLeave";
import LeaveComment from "./DetailsParts/LeaveComment";

interface Props {
  selectedEntry: EditEntry | Entry | null;
  leaveEntity: LeaveEntity;
  setLeaveEntity: (val: LeaveEntity) => void;
  entries: AbsenceListEntry[];
  setEntries: (val: AbsenceListEntry[]) => void;
  updateLeaveEntity: (
    key: string,
    value: string | LeaveEntityDateRange | null | Leave,
  ) => void;
  editingActive: boolean;
  translatedEntryTypes: { [key: string]: string };
  inputErrors: { [code: string]: boolean };
  setInputErrors: (inputErrors: { [code: string]: boolean }) => void;
  pendingAttachments?: File[];
  onPendingAttachmentsChange?: (files: File[]) => void;
}

const GeneralDetails = ({
  selectedEntry,
  leaveEntity,
  setLeaveEntity,
  entries,
  setEntries,
  updateLeaveEntity,
  editingActive,
  translatedEntryTypes,
  inputErrors,
  setInputErrors,
  pendingAttachments,
  onPendingAttachmentsChange,
}: Props) => {
  const { t } = useTranslation();

  // Assignments from state (fetched by date range for the current talent)
  const rawAssignments = useAppSelector(selectAssignments);

  const memoizedAssignments = useMemo(() => {
    return rawAssignments;
  }, [rawAssignments]);

  // General type of entries that can be switched between each other
  const switchableEntryTypes = [
    "PAID_ABSENCE",
    "UNPAID_ABSENCE",
    "MEDICAL_CHECK",
    "MANDATORY_HEALTH_CHECK",
    "TRAINING_PAID",
    "TRAINING_UNPAID",
    "MILITARY_REFRESHER",
    "MILITARY_DRAFT",
    "UNAUTHORIZED_ABSENCE",
    "WEDDING",
    "FUNERAL",
    "BIRTHDAY_50TH",
    "BIRTHDAY_60TH",
    "BIRTHDAY_70TH",
    "FORCE_MAJEURE_ABSENCE",
    "JOB_ACTION_OF_OTHER_EMPLOYEES",
    "FAMILY_FORCE_MAJEURE_ABSENCE",
    "LABOUR_UNION_MEETING",
    "LABOUR_UNION_TRAINING_PAID",
    "LABOUR_UNION_TRAINING_UNPAID",
    "MUNCIPAL_DUTIES",
    "APPRENTICESHIP_TRAINING",
    "ELECTION_DUTIES",
    "STUDY_LEAVE",
    "UNSUDDEN_MEDICAL_CHECK",
    "UNSUDDEN_MANDATORY_HEALTH_CHECK",
    "UNSUDDEN_PAID_ABSENCE",
    "UNSUDDEN_UNPAID_ABSENCE",
    "SUDDEN_DENTAL_DISEASE",
    "VOCATIONAL_QUALIFICATION",
    "STRIKE",
  ];

  // Get all assignmentIds from checked entries
  const allAssignmentIdsFromCheckedEntries = useMemo(
    () =>
      entries
        .filter((entry) => entry.checked)
        .map((entry) => entry.assignmentId),
    [entries],
  );

  // Filter assignments based on assignment IDs from checked entries
  const filteredAssignments = useMemo(
    () =>
      memoizedAssignments.filter((assignment) =>
        allAssignmentIdsFromCheckedEntries.includes(assignment.assignmentId),
      ),
    [memoizedAssignments, allAssignmentIdsFromCheckedEntries],
  );

  // Memoized calculation for availableAbsenceTypes
  const availableAbsenceTypes = useMemo(() => {
    // Find the common entryTypes between all assignments
    const allAvailableEntryTypes = filteredAssignments.reduce(
      (acc, assignment) => {
        return assignment.details.availableEntryTypes.filter((type) =>
          acc.includes(type),
        );
      },
      filteredAssignments[0]?.details?.availableEntryTypes || [],
    );

    return filterAbsenceTypes(
      translatedEntryTypes,
      allAvailableEntryTypes,
      [],
      // Only limit the list of available types if the current type is in switchableEntryTypes
      switchableEntryTypes.includes(leaveEntity.type)
        ? switchableEntryTypes
        : [],
    );
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    translatedEntryTypes,
    switchableEntryTypes,
    memoizedAssignments,
    allAssignmentIdsFromCheckedEntries,
    leaveEntity?.type,
  ]);

  // Check if the current type is in the list of available types
  useEffect(() => {
    if (availableAbsenceTypes?.includes(leaveEntity.type || "")) {
      inputErrors.type !== false &&
        setInputErrors({
          ...inputErrors,
          type: false,
        });
    } else {
      inputErrors.type !== true &&
        setInputErrors({
          ...inputErrors,
          type: true,
        });
    }
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableAbsenceTypes]);

  return (
    selectedEntry && (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#FFFFFF",
          borderRadius: "8px",
          padding: "24px 16px",
          width: "28%",
          height: "100%",
          opacity: editingActive ? 0.6 : 1, // Adjust opacity, if entry is being edited
          pointerEvents: editingActive ? "none" : "auto", // Disable pointer events, if entry is being edited
        }}
      >
        <DialogTitle
          sx={{
            margin: "0px",
            padding: "0px",
          }}
        >
          <Typography
            variant="header"
            sx={{
              fontWeight: 600,
            }}
          >
            {t("hourEntries:absenceDetails").toUpperCase()}
          </Typography>
          <Divider
            sx={{
              marginTop: "16px",
              marginBottom: "16px",
            }}
          />
        </DialogTitle>
        <Typography
          variant="header"
          sx={{
            fontSize: "12px",
            fontWeight: 400,
            color: "rgba(0, 0, 0, 0.52)",
            marginBottom: "5px",
          }}
        >
          {t("hourEntries:absenceType")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <Select
            // Disable select only if a type is set but not in the list of switchableEntryTypes
            disabled={
              leaveEntity.type
                ? !switchableEntryTypes.includes(leaveEntity.type)
                : false // Allow changing if no type is set
            }
            size="small"
            error={inputErrors.type}
            fullWidth
            value={leaveEntity.type || ""}
            renderValue={() => translatedEntryTypes[leaveEntity.type] || ""}
            sx={{ marginRight: "5px" }}
            onChange={(e) => {
              updateLeaveEntity("type", e.target.value as string);
            }}
          >
            {/* Conditionally render options based on whether the type is set and switchable */}
            {!leaveEntity.type ||
            switchableEntryTypes.includes(leaveEntity.type) ? (
              // Render full list of options when there's no type set or it's switchable
              availableAbsenceTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {translatedEntryTypes[type] || type}
                </MenuItem>
              ))
            ) : (
              // Render only the current type as a disabled option when not switchable
              <MenuItem value={leaveEntity.type} disabled>
                {translatedEntryTypes[leaveEntity.type] || leaveEntity.type}
              </MenuItem>
            )}
          </Select>
          <InfoTooltip
            title={t("hourEntries:absenceTypeTooltipGeneralLeave")}
          />
        </Box>
        {inputErrors.type && (
          <Typography
            variant="header"
            sx={{ color: "#FF505A", fontSize: "12px", marginTop: "5px" }}
          >
            {t("hourEntries:pickCompatibleAbsenceType")}
          </Typography>
        )}

        <ToGeneralLeave
          leaveEntity={leaveEntity}
          setLeaveEntity={setLeaveEntity}
          entries={entries}
          setEntries={setEntries}
          updateLeaveEntity={updateLeaveEntity}
          inputErrors={inputErrors}
          setInputErrors={setInputErrors}
          showAttachments={true}
          pendingAttachments={pendingAttachments}
          onPendingAttachmentsChange={onPendingAttachmentsChange}
        />

        {leaveEntity?.comment && <LeaveComment comment={leaveEntity.comment} />}
      </Box>
    )
  );
};

export default GeneralDetails;
